/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMasterParams, useOdnTypes } from "@/api/master-param";
import { MediaData, useBpmnMediaTempCreate } from "@/api/media";
import { useGenerateRFSInvalidXlsx, useRFS, useRFSRevise } from "@/api/rfs";
import { useAPDs } from "@/api/trackerdata-apd";
import { FieldCombobox } from "@/components/form/field-combobox";
import { FieldFile } from "@/components/form/field-file";
import { FieldInput } from "@/components/form/field-input";
import { FieldInputDate } from "@/components/form/field-input-date";
import { FieldSelect } from "@/components/form/field-select";
import { FieldTextarea } from "@/components/form/field-textarea";
import {
  FieldsetThreeColumn,
  FormContainer,
  Legend,
} from "@/components/form/form-container";
import { FormFooter } from "@/components/form/form-footer";
import {
  DefaultButton,
  DownloadButton,
  SubmitButton,
} from "@/components/ui/button";
import { VerticalGaps } from "@/components/ui/container";
import { InputWithLabel } from "@/components/ui/input-with-label";
import { H2 } from "@/components/ui/typography";
import { MISSING_ID_IN_ROUTE, REQUIRED_MESSAGE, ROLE } from "@/utils/constant";
import { swalError, swalSuccess } from "@/utils/lib/swal";
import { zodResolver } from "@hookform/resolvers/zod";
import { AxiosError } from "axios";
import { useEffect, useId, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { z } from "zod";
import { RFSInvalidTable } from "./RFSInvalidTable";
import { useCurrentUser } from "../auth/useCurrentUser";
import { useProfileByKeycloakId } from "@/api/auth";
import { useCancelRevise } from "./utils/useCancelRevise";
import {
  FieldHomepassAdditional,
  FieldHomepassInvalid,
} from "@/features/rfs/document/ui.tsx";
import { useProjectByCLUSTERID } from "@/api/project-monitoring";

const rfsEditFormSchema = z.object({
  clusterId: z.string().min(1, { message: REQUIRED_MESSAGE }),
  projectId: z.string().min(1, { message: REQUIRED_MESSAGE }),
  odnType: z.string().min(1, { message: REQUIRED_MESSAGE }),
  media: z
    .any()
    .optional()
    .superRefine((value, ctx) => {
      // allow undefined (optional)
      if (value === undefined) return;

      // ensure it's a FileList
      if (!(value instanceof FileList)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: REQUIRED_MESSAGE,
        });
        return;
      }

      // ensure at least one file present
      if (value.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: REQUIRED_MESSAGE,
        });
        return;
      }

      // guard access to name and validate extension
      const name = value[0]?.name;
      if (typeof name !== "string" || !name.endsWith(".kmz")) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Please upload files in .kmz format only",
        });
      }
    }),
  totalBizzHomepass: z
    .number()
    .or(
      z.string().min(1, {
        message: REQUIRED_MESSAGE,
      }),
    )
    .pipe(z.coerce.number()),
  totalResidenceHomepass: z
    .number()
    .or(
      z.string().min(1, {
        message: REQUIRED_MESSAGE,
      }),
    )
    .pipe(z.coerce.number()),
  dateFeederDone: z.string().optional(),
  dateOltDone: z.string().optional(),
  remarks: z.string().optional(),
  fdtType: z.string().min(1, { message: REQUIRED_MESSAGE }),
  category: z.string().min(1, { message: REQUIRED_MESSAGE }),

  // conditional fields (optional in schema; validated in handleSubmit)
  homepassInvalid: z
    .number()
    .or(z.string().optional())
    .optional()
    .transform((v) => (v === undefined || v === "" ? undefined : Number(v))),
  homepassAdditional: z
    .number()
    .or(z.string().optional())
    .optional()
    .transform((v) => (v === undefined || v === "" ? undefined : Number(v))),
});
type RFSEditFormSchema = z.infer<typeof rfsEditFormSchema>;

const getStorageKey = (id: string) => `rfs_edit_form_${id}`;

export function RFSEditForm() {
  const [clusterIdSearch, setClusterIdSearch] = useState("");
  const [projectIdSearch, setProjectIdSearch] = useState("");
  const [formInitialized, setFormInitialized] = useState(false);

  const navigate = useNavigate();
  const user = useCurrentUser();
  const { data: profile } = useProfileByKeycloakId({ keycloakId: user.id });
  const userCompanyCode = profile?.companyCode;
  const isVendor = user.roles.includes(ROLE.VENDOR);

  const { mutateAsync: mutateMedia } = useBpmnMediaTempCreate();
  const { mutate: mutateRfs, isPending } = useRFSRevise();

  const { data: odnTypes } = useOdnTypes();
  const odnTypesOptions = odnTypes?.content?.map((odnType) => ({
    value: odnType.value,
    label: odnType.value,
  }));

  const { mutate: generateRFSInvalid, isPending: isDownloading } =
    useGenerateRFSInvalidXlsx();

  const formId = useId();

  const { id } = useParams();
  if (!id) {
    throw new Error(MISSING_ID_IN_ROUTE);
  }

  // Get the RFS data
  const storageKey = getStorageKey(id);

  /* ===== RFS Detail ===== */
  const { data: rfs, isLoading: isRfsLoading } = useRFS({ id });
  /* =============== */
  /* ===== Cancel Hook ===== */
  const { handleCancel } = useCancelRevise({ id });
  /* =============== */

  // Initialize the form
  const form = useForm<RFSEditFormSchema>({
    resolver: zodResolver(rfsEditFormSchema),
    defaultValues: {
      clusterId: "",
      projectId: "",
      odnType: "",
      totalResidenceHomepass: 0,
      totalBizzHomepass: 0,
    },
    mode: "onChange",
  });

  useEffect(() => {
    if (!formInitialized && !isRfsLoading && rfs) {
      // Check localStorage first
      const savedValues = localStorage.getItem(storageKey);
      let initialValues;

      if (savedValues) {
        // Use saved values from localStorage
        initialValues = JSON.parse(savedValues);
      } else {
        // Fall back to API data
        initialValues = {
          clusterId: rfs?.clusterId ?? "",
          projectId: rfs?.projectId ?? "",
          odnType: rfs?.odnType ?? "",
          totalResidenceHomepass: rfs?.totalResidenceHomepass ?? 0,
          totalBizzHomepass: rfs?.totalBizzHomepass ?? 0,
          dateFeederDone: rfs?.dateFeederDone ?? "",
          dateOltDone: rfs?.dateOltDone ?? "",
          remarks: rfs?.remarks ?? "",
          fdtType: rfs?.fdtType ?? "",
          category: rfs?.category ?? "",
        };
      }

      // Reset the form with the values
      form.reset(initialValues);
      setFormInitialized(true);
    }
  }, [form, rfs, isRfsLoading, formInitialized, storageKey]);

  // Save the form data to localStorage
  const formValues = form.watch();
  useEffect(() => {
    if (formInitialized) {
      localStorage.setItem(storageKey, JSON.stringify(formValues));
    }
  }, [formValues, formInitialized, storageKey]);

  /* ===== APD ===== */
  const { data: apds } = useAPDs({
    vendor: isVendor ? userCompanyCode : undefined,
    search: clusterIdSearch,
  });
  const { data: adpByClusterIds } = useAPDs({
    vendor: isVendor ? userCompanyCode : undefined,
    clusterId: rfs?.clusterId,
  });
  const apdsContent = apds?.content || [];
  const apdsContentMap = new Map(apdsContent.map((obj) => [obj?.id, obj]));
  if (adpByClusterIds) {
    const newItem = adpByClusterIds.content[0];

    if (!apdsContentMap.has(newItem?.id)) {
      apdsContent.push(newItem); // Tambahkan ke array jika belum ada
      apdsContentMap.set(newItem?.id, newItem); // Update map
    }
  }
  const clusterIdOptions = optionsFromProperty<{ clusterId: string }>(
    apdsContent || [],
    "clusterId",
  );
  const currentClusterId = form.watch("clusterId");
  const apdsFilteredByClusterId = apdsContent?.filter(
    (apd) => apd?.clusterId === currentClusterId,
  );

  const apd =
    (apdsFilteredByClusterId ?? []).length === 1
      ? apdsFilteredByClusterId?.[0]
      : undefined;
  const apdVendorCode = apd?.vendor;
  /* =============== */

  /* ===== Project ===== */
  const { data: projects } = useProjectByCLUSTERID({
    ClusterID: currentClusterId,
  });
  const { data: dataProjects } = useMasterParams(
    {
      paramType: "VENDOR_INHOUSE",
      active: "true",
      asc: "true",
      paramTag: apdVendorCode,
    },
    {
      enabled: !!apdVendorCode,
    },
  );

  const projectIdsOptions =
    projects?.map((project) => ({
      value: project.ProjectID,
      label: project.ProjectID,
    })) ?? [];
  const vendorInhouseProjectIds =
    dataProjects?.content.map((item) => ({
      value: item.value,
      label: item.value,
    })) ?? [];

  const isVendorProjectIdOptionsAvailable = vendorInhouseProjectIds.length > 0;

  const currentProjectId = form.watch("projectId");
  const currentProject = isVendorProjectIdOptionsAvailable
    ? dataProjects?.content.find(
        (project) => project.value === currentProjectId,
      )
    : projects?.find((project) => project.ProjectID === currentProjectId);
  const currentProjectDescription = isVendorProjectIdOptionsAvailable
    ? (currentProject as any)?.value
    : (currentProject as any)?.ProjectDescription;
  /* =================== */

  /* ===== Total HP RFS ===== */
  const totalResidenceHomepass = parseInt(
    (form.watch("totalResidenceHomepass") as unknown as string) || "0",
    10,
  );
  const totalBizzHomepass = parseInt(
    (form.watch("totalBizzHomepass") as unknown as string) || "0",
    10,
  );
  const totalHpRfs = (totalResidenceHomepass + totalBizzHomepass).toString();
  /* ======================== */

  const hpApdInt = parseInt((apd?.hpDesign as unknown as string) || "0", 10); // NOSONAR
  const totalHpRfsInt = totalResidenceHomepass + totalBizzHomepass;

  const clusterId = form.watch("clusterId");

  function handleDownload() {
    generateRFSInvalid(
      { id, name: `RFS Validation Result-${clusterId}.xlsx` },
      {
        onError: () => {
          swalError.fire();
        },
      },
    );
  }

  // read reactive validity
  const {
    formState: { isValid },
  } = form;

  const watchedResidence = Number(form.watch("totalResidenceHomepass") ?? 0);
  const watchedBizz = Number(form.watch("totalBizzHomepass") ?? 0);

  const totalRfs = watchedResidence + watchedBizz;
  const hpApd = parseInt((apd?.hpDesign as unknown as string) || "0", 10); // NOSONAR

  const needInvalid = totalRfs < hpApd;
  const needAdditional = totalRfs > hpApd;
  const requiredInvalidCount = needInvalid ? hpApd - totalRfs : 0;
  const requiredAdditionalCount = needAdditional ? totalRfs - hpApd : 0;

  /*
    Side-effect: update/clear form errors when watched values change.
    Avoid doing this directly during render to prevent render loops.
  */
  const watchedHomepassInvalid = form.watch("homepassInvalid");
  const watchedHomepassAdditional = form.watch("homepassAdditional");

  /* v8 ignore start */
  useEffect(() => {
    // If APD not loaded yet, skip
    if (apd === undefined) return;

    if (needInvalid) {
      // live value from watch
      const v = watchedHomepassInvalid;
      if (v !== undefined && Number(v) === requiredInvalidCount) {
        // correct value -> clear error
        form.clearErrors("homepassInvalid");
      } else {
        // missing or incorrect -> set error
        form.setError("homepassInvalid", {
          type: "manual",
          message: `Total Homepass Invalid must be ${requiredInvalidCount}`,
        });
      }
      // always clear the other conditional error when this branch is active
      form.clearErrors("homepassAdditional");
    } else if (needAdditional) {
      const v = watchedHomepassAdditional;
      if (v !== undefined && Number(v) === requiredAdditionalCount) {
        form.clearErrors("homepassAdditional");
      } else {
        form.setError("homepassAdditional", {
          type: "manual",
          message: `Total Homepass Additional must be ${requiredAdditionalCount}`,
        });
      }
      form.clearErrors("homepassInvalid");
    } else {
      // no diff => clear both
      form.clearErrors(["homepassInvalid", "homepassAdditional"]);
    }
    // dependencies: include watched values so effect runs on user input
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    watchedResidence,
    watchedBizz,
    watchedHomepassInvalid,
    watchedHomepassAdditional,
    apd?.hpDesign,
    needInvalid,
    needAdditional,
    requiredInvalidCount,
    requiredAdditionalCount,
  ]);

  async function handleSubmit(values: RFSEditFormSchema) {
    if (!id) {
      throw new Error(MISSING_ID_IN_ROUTE);
    }

    if (!apd) {
      throw new Error("APD not found");
    }

    // conditional validations
    if (needInvalid) {
      const provided = values.homepassInvalid;
      if (provided === undefined || Number(provided) !== requiredInvalidCount) {
        form.setError("homepassInvalid", {
          type: "manual",
          message: `Total Homepass Invalid must be ${requiredInvalidCount}`,
        });
        return;
      }
    }

    if (needAdditional) {
      const provided = values.homepassAdditional;
      if (
        provided === undefined ||
        Number(provided) !== requiredAdditionalCount
      ) {
        form.setError("homepassAdditional", {
          type: "manual",
          message: `Total Homepass Additional must be ${requiredAdditionalCount}`,
        });
        return;
      }
    }

    mutateMedia(
      {
        data: {
          file: values.media![0],
          target: "/abd-document",
        },
      },
      {
        onSuccess: (result) => {
          onMutateRFS(values, result);
        },
        onError: (error) => {
          const err = error as AxiosError;
          const errorMessage = (err.response?.data as { data: unknown })?.data;

          swalError
            .fire({
              title: "Error",
              text: (errorMessage as string) ?? "Failed to submit RFS",
            })
            .then((response) => {
              if (response.isConfirmed) {
                window.location.reload();
              }
            });
        },
      },
    );
  }

  const onMutateRFS = (values: RFSEditFormSchema, result: MediaData) =>
    mutateRfs(
      {
        id,
        data: {
          odnType: values.odnType,
          projectId: values.projectId,
          trackerdataApd: { id: apd!.id },
          projectDescription: currentProjectDescription,
          media: {
            id: result.id,
            path: result.path,
            originalFileName: result.originalFileName,
            name: result.name,
            type: result.type,
          },
          totalResidenceHomepass: values.totalResidenceHomepass,
          totalBizzHomepass: values.totalBizzHomepass,
          dateFeederDone: values.dateFeederDone,
          dateOltDone: values.dateOltDone,
          remarks: values.remarks,
          fdtType: values.fdtType,
          category: values.category,
        },
        params: {
          withVariable: "true",
        },
      },
      {
        onSuccess: () => {
          // Delete the saved form data
          localStorage.removeItem(storageKey);

          swalSuccess
            .fire({
              title: "Submission Successful",
              text: "You have successfully submitted on RFS application",
            })
            .then(() => {
              navigate("/rfs/rfs");
            });
        },
        onError: (error) => {
          const err = error as AxiosError;
          if (err.code === "ERR_BAD_REQUEST") {
            const errorMessage = (err.response?.data as { data: unknown })
              ?.data;

            swalError
              .fire({
                title: "RFS REVISION",
                text: (errorMessage as string) ?? "Failed to submit RFS",
              })
              .then((response) => {
                if (response.isConfirmed) {
                  window.location.reload();
                }
              });
          } else {
            const errorMessage = (err.response?.data as { data: unknown })
              ?.data;

            swalError
              .fire({
                text: `${errorMessage}`,
              })
              .then((response) => {
                if (response.isConfirmed) {
                  window.location.reload();
                }
              });
          }
        },
      },
    );

  /* v8 ignore stop */

  /* ===== Options ===== */
  const { data: fdtTypes } = useMasterParams({
    paramType: "ITV_FDT_TYPE",
    active: "true",
    asc: "true",
  });

  const fdtTypeOptions = fdtTypes?.content.map((status) => ({
    value: status.value,
    label: status.value,
  }));

  const { data: dataCategories } = useMasterParams({
    paramType: "ITV_CATEGORY_DATA",
    active: "true",
    asc: "true",
  });

  const dataCategoryOptions = dataCategories?.content.map((status) => ({
    value: status.value,
    label: status.value,
  }));

  /* =============== */

  return (
    <>
      <H2>Draft (Invalid Data) - {clusterId}</H2>
      <FormContainer id={formId} form={form} onSubmit={handleSubmit}>
        <FieldsetThreeColumn>
          <Legend>Input by master APD & SAP</Legend>

          <VerticalGaps>
            <FieldCombobox
              name="clusterId"
              label="Cluster ID"
              search={clusterIdSearch}
              setSearch={setClusterIdSearch}
              options={clusterIdOptions ?? []}
              hasRequiredAsterisk
            />
          </VerticalGaps>

          <FieldCombobox
            name="projectId"
            label="Project ID"
            search={projectIdSearch}
            setSearch={setProjectIdSearch}
            options={
              isVendorProjectIdOptionsAvailable
                ? vendorInhouseProjectIds
                : projectIdsOptions
            }
            hasRequiredAsterisk
          />

          <FieldSelect
            name="odnType"
            label="ODN Type"
            options={odnTypesOptions ?? []}
            hasRequiredAsterisk
          />
        </FieldsetThreeColumn>

        <FieldsetThreeColumn>
          <Legend>Autofill</Legend>

          <VerticalGaps>
            <InputWithLabel
              value={apd?.suffixPermit}
              label="Permit ID"
              isDisabled
            />
            <InputWithLabel value={apd?.fdtId} label="FDTID" isDisabled />
            <InputWithLabel
              value={apd?.oltIdaction}
              label="OLT-ID"
              isDisabled
            />
            <InputWithLabel value={apd?.hpDesign} label="HP APD" isDisabled />
          </VerticalGaps>

          <VerticalGaps>
            <InputWithLabel
              value={apd?.cityTown}
              label="City Town"
              isDisabled
            />
            <InputWithLabel
              value={apd?.projectName}
              label="Project Name"
              isDisabled
            />
            <InputWithLabel
              value={currentProjectDescription}
              label="Project Description"
              isDisabled
            />
          </VerticalGaps>

          <VerticalGaps>
            <InputWithLabel
              value={apd?.drawingIdApd}
              label="Drawing ID APD"
              isDisabled
            />
            <InputWithLabel value={apd?.oltType} label="OLT Type" isDisabled />
            <InputWithLabel
              value={apd?.combineStatus}
              label="Combine Status"
              isDisabled
            />
          </VerticalGaps>
        </FieldsetThreeColumn>

        <FieldsetThreeColumn>
          <Legend>Input by Vendor</Legend>

          <div className="grid grid-cols-3 gap-4">
            <VerticalGaps>
              <FieldInput
                name="totalResidenceHomepass"
                label="Total Residence Homepass"
                type="number"
                hasRequiredAsterisk
              />
              <FieldInputDate label="Date Feeder Done" name="dateFeederDone" />
              <FieldInputDate label="Date OLT Done" name="dateOltDone" />
              <FieldTextarea label="Remarks" name="remarks" />
            </VerticalGaps>

            <VerticalGaps>
              <FieldInput
                name="totalBizzHomepass"
                label="Total Bizz Homepass"
                type="number"
                hasRequiredAsterisk
              />
              <FieldHomepassInvalid
                hpApd={hpApdInt}
                hpRfs={totalHpRfsInt}
                apdId={apd?.id}
                isDisabled={needInvalid}
              />
              <FieldSelect
                name="fdtType"
                label="FDT Type"
                options={fdtTypeOptions ?? []}
                hasRequiredAsterisk
              />
            </VerticalGaps>

            <VerticalGaps>
              <InputWithLabel
                value={totalHpRfs}
                label="Total HP RFS (ABD)"
                isDisabled
                hasRequiredAsterisk
              />
              <FieldHomepassAdditional
                hpApd={hpApdInt}
                hpRfs={totalHpRfsInt}
                apdId={apd?.id}
                isDisabled={needAdditional}
              />
              <FieldSelect
                name="category"
                label="Category Data"
                options={dataCategoryOptions ?? []}
                hasRequiredAsterisk
              />
            </VerticalGaps>
            <div className="col-span-full">
              <FieldFile
                label="Upload document ABD.kmz"
                name="media"
                accept=".kmz"
                defaultName={rfs?.media?.name}
                hasRequiredAsterisk
              />
            </div>
          </div>
        </FieldsetThreeColumn>
      </FormContainer>
      <FormFooter>
        <DefaultButton
          mood="axiata-reddish-outline"
          type="button"
          onClick={handleCancel}
        >
          Cancel
        </DefaultButton>
        <SubmitButton form={formId} loading={isPending} disabled={!isValid} />
      </FormFooter>

      <div className="text-right">
        <DownloadButton onClick={handleDownload} loading={isDownloading}>
          Download Validation Result
        </DownloadButton>
      </div>

      <RFSInvalidTable rfsId={id} />
    </>
  );
}
function optionsFromProperty<T>(data: T[], property: keyof T) {
  const result = [];
  const existing = new Set();
  for (const datum of data) {
    if (!existing.has(datum?.[property])) {
      result.push({
        value: datum?.[property],
        label: datum?.[property] ?? "",
      });
      existing.add(datum?.[property]);
    }
  }

  return result;
}
